import Shaka from 'shaka-player'
// eslint-disable-next-line import/no-cycle
import HTML5Player from '../HTML5Player'
import { PLAYER_ERROR_CODES, PLAYER_REPORTABLE_EVENTS } from '../constants'

export default class ShakaPBSPlayer {
  _shaka = null
  _playerRef = null
  _contentUrl
  _setCaptionsAvailable

  _initializeListeners() {
    this._shaka
      .getNetworkingEngine()
      .registerRequestFilter(async (_, request) => {
        if (request.method === 'HEAD') {
          // HEAD requests respond with 404 with some assets
          request.method = 'GET'
        }
      })

    const eventManager = new Shaka.util.EventManager()
    eventManager.listen(this._shaka, 'error', (error) => {
      console.error('Shaka error', error)
      const { severity, category } = error?.detail || {}

      if (severity !== Shaka.util.Error.Severity.RECOVERABLE) {
        // CRITICAL: 2, RECOVERABLE : 1
        // Check if this is a network error (category 1 is NETWORK)
        // Using numeric value 1 directly since Shaka.util.Error.Category.NETWORK = 1
        if (category === 1) {
          this._hadNetworkError = true
        }

        this._reportEvent(PLAYER_REPORTABLE_EVENTS.ERROR, {
          code: PLAYER_ERROR_CODES.MEDIA,
          message: 'Shaka error',
          data: error.detail,
        })
      }
    })

    eventManager.listen(this._shaka, 'adaptation', (_, newLevel) => {
      this._reportEvent(PLAYER_REPORTABLE_EVENTS.RENDITION_CHANGE, {
        bitrate: newLevel.bitrate,
        width: newLevel.width,
        height: newLevel.height,
      })
    })

    // Listen only once to timeUpdate to know if there are captions available
    eventManager.listenOnce(this._playerRef.current, 'timeupdate', () => {
      const captionsAvailable = this._shaka.getTextTracks().length > 0
      this._setCaptionsAvailable(captionsAvailable)
    })

    // Listen for Shaka's 'buffering' event to detect buffer depletion
    // This event fires with buffering=true when Shaka is buffering/stalled
    eventManager.listen(this._shaka, 'buffering', (event) => {
      const isBuffering = event.buffering

      // Only report buffer depletion if we're buffering AND had a network error
      // This prevents normal startup buffering from triggering error handling
      if (isBuffering && this._hadNetworkError) {
        this._bufferDepleted = true
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.BUFFER_DEPLETED)
      } else if (!isBuffering && this._bufferDepleted) {
        // Buffering ended after we had buffer depletion - player recovered!
        this._hadNetworkError = false
        this._bufferDepleted = false
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.BUFFER_RECOVERED)
      }
    })
  }

  constructor(
    playerRef,
    {
      contentSourceUrl,
      reportPlayerEvent,
      setCaptionsAvailable,
      drmWidevideLicenseUrl,
    }
  ) {
    this._playerRef = playerRef
    this._contentUrl = contentSourceUrl
    this._reportEvent = reportPlayerEvent || (() => {})
    this._setCaptionsAvailable = setCaptionsAvailable || (() => {})
    this.isInitialized = false
    this._drmWidevideLicenseUrl = drmWidevideLicenseUrl
    this._hadNetworkError = false
    this._bufferDepleted = false
    Shaka.polyfill.installAll()
  }

  init() {
    this._shaka = new Shaka.Player()
    this._shaka.attach(this._playerRef.current)
    this._shaka.configure({
      drm: {
        servers: {
          'com.widevine.alpha': this._drmWidevideLicenseUrl,
        },
      },
    })
    this._initializeListeners()
    if (this._contentUrl) {
      this.load(this._contentUrl)
    }
  }

  load(source) {
    this._shaka
      .load(source)
      .then(() => {
        this._shaka.setTextTrackVisibility(true)
        this.isInitialized = true
        this.play()
      })
      .catch((error) => {
        console.error(error)
        this._reportEvent(PLAYER_REPORTABLE_EVENTS.ERROR, {
          code: PLAYER_ERROR_CODES.MEDIA,
          message: 'Shaka player fatal mediaError',
          data: error,
        })
      })
  }

  play() {
    this._playerRef.current?.play()
  }

  // eslint-disable-next-line class-methods-use-this
  recoverFromNetworkError() {
    // shaka-player recovers automatically from network errors
    return Promise.resolve(true)
  }

  pause() {
    this._playerRef.current?.pause()
  }

  seek(newCurrentTime) {
    this._playerRef.current.currentTime = newCurrentTime
  }

  isReady() {
    return this._playerRef.current?.readyState >= 3
  }

  destroy() {
    if (this.isInitialized) {
      this.isInitialized = false
      this._shaka.destroy()
    }
  }

  // TODO: the 2 methods below are maybe static methods
  // eslint-disable-next-line class-methods-use-this
  getDOMImplementation() {
    return HTML5Player
  }

  // eslint-disable-next-line class-methods-use-this
  getVersion() {
    return Shaka.Player.version
  }
}
